"use client"

import { useState, useEffect } from "react"
import { Link, useNavigate, useLocation } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import { Modal } from "react-bootstrap"
import parse from "html-react-parser"
import { User, Mail, Lock, Eye, EyeOff, Globe, KeyRound, ArrowLeft, Leaf, X, Info, Phone, ArrowRight, CheckCircle } from "lucide-react"
import { notify } from "../../../utility/Helper1"
import { userSelect, allRemoteSettingsSelect } from "../../../redux/selectors/index"
import { authActions } from "../../../redux/actions"
import { staticCountries } from "../../../utility/countries"
import ReCAPTCHA from "../../../utility/customComponents/ReCAPTCHA"
import AuthHolder from "./AuthHolder";

// Get referral from localStorage
const referralToUse = localStorage?.getItem("ref") != "null" ? localStorage?.getItem("ref") : ""

const SignupPage = ({ activeTab, setActiveTab }) => {
  const dispatch = useDispatch()
  const users = useSelector(userSelect)
  const site_settings = useSelector(allRemoteSettingsSelect)

  // Form states
  const [username, setUsername] = useState("")
  const [email, setEmail] = useState("")
  const [withdrawalPin, setWithdrawalPin] = useState("")
  const [showPin, setShowPin] = useState(false)
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [country, setCountry] = useState(0)
  const [phone, setPhone] = useState("")
  const [phoneCode, setPhoneCode] = useState("...")
  const [referral, setReferrals] = useState(referralToUse)
  const [agree, setAgree] = useState(false)
  const [walletAddress, setWalletAddress] = useState("")
  const [bank, setBankName] = useState("")
  const [accountName, setAccountName] = useState("")
  const [accountNumber, setAccountNumber] = useState("")
  const [depositMethod, setDepositMethod] = useState(100)

  // UI states
  const [loading, changeLoading] = useState(false)
  const [loadingText, setLoadingText] = useState("")
  const [fullPageLoading, setFullPageLoading] = useState(false)
  const [captcha, setCaptcha] = useState(null)
  const [show, setShow] = useState(false)
  const [focusedField, setFocusedField] = useState('')

  const navigate = useNavigate()
  const location = useLocation()
  const search = location.search
  const referalLink = new URLSearchParams(search).get("ref")

  // Handle captcha change
  const captchaHasChanged = (data) => {
    setCaptcha(data)
  }

  // Modal handlers
  const handleClose = () => setShow(false)
  const handleShow = () => setShow(true)

  // Check for referral in URL
  useEffect(() => {
    if (referalLink != "" && referalLink != null && referalLink != "null") {
      localStorage.setItem("ref", referalLink)
      setReferrals(referalLink)
    }
  }, [referalLink])

  // Toggle agreement checkbox
  const checkboxHandler = () => {
    setAgree(!agree)
  }

  // Handle country change
  const changeCountry = (countryId) => {
    const selectedCountry = staticCountries.filter((e) => e.id == countryId)
    setCountry(countryId)
    const countryPhoneCode = selectedCountry?.[0]?.phonecode
    setPhoneCode(countryPhoneCode)
  }

  // Handle registration
  const doRegister = async (e) => {
    e.preventDefault()

    // Check terms agreement if required
    if (!agree && (site_settings?.site_settings?.term ?? "") != "") {
      notify("You must accept the terms and conditions to continue", 0)
      return
    }

    // Check captcha if required
    if (!captcha && Number(site_settings?.site_settings?.use_google_reCaptcha) == 1) {
      notify("Invalid captcha response", 0)
      return
    }

    setFullPageLoading(true)
    changeLoading(true)
    setLoadingText("Creating account...")

    // Prepare payload
    const payLoad = {
      captcha,
      depositMethod,
      username,
      email,
      password,
      country,
      phone,
      withdrawalPin,
      referral,
    }

    // Add financial details based on deposit method
    let financeLoad
    if (depositMethod == 0) {
      financeLoad = { bank, accountName, accountNumber }
    } else {
      financeLoad = { walletAddress }
    }

    // Dispatch signup action
    dispatch(
      authActions.onSignUp("account/register", Object.assign({}, payLoad, financeLoad), (response) => {
        if (response.code != 0) {
          localStorage.removeItem("ref")
          navigate("/verify-user", { replace: true })
        } else {
          notify(response.msg, response.code)
        }

        changeLoading(false)
        setLoadingText("")
        setFullPageLoading(false)
      }),
    )
  }

  // Check if registration is open or invitation-only
  const openRegBasedOnRefSett =
    ((site_settings?.operational_settings?.useCompulsoryReferral ?? 0) == 1 && referral != "" && referral != null) ||
    (site_settings?.operational_settings?.useCompulsoryReferral ?? 0) == 0

  return (
    <AuthHolder title="Sign Up">
      {/* Full page loading overlay */}
      {fullPageLoading && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-gray-900/90 rounded-2xl p-8 border border-amber-500/20">
            <div className="flex items-center space-x-4">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-400 border-t-transparent"></div>
              <span className="text-white font-medium">{loadingText}</span>
            </div>
          </div>
        </div>
      )}

      {openRegBasedOnRefSett ? (
        <form onSubmit={doRegister} className="space-y-6">
          {/* Username Field */}
          <div className="space-y-2">
            <div className="relative">
              <input
                type="text"
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onFocus={() => setFocusedField('username')}
                onBlur={() => setFocusedField('')}
                required
                className="w-full h-14 pl-6 pr-6 bg-gray-800/50 border border-gray-600/50 rounded-2xl text-white placeholder-gray-400 focus:border-amber-500/70 focus:bg-gray-800/70 focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all duration-300"
              />
              <div className={`absolute left-6 top-1/2 transform -translate-y-1/2 transition-all duration-300 ${
                focusedField === 'username' || username ? 'opacity-100 scale-110' : 'opacity-0 scale-90'
              }`}>
                <div className="w-1 h-1 bg-amber-400 rounded-full"></div>
              </div>
            </div>
          </div>

          {/* Email Field */}
          <div className="space-y-2">
            <div className="relative">
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setFocusedField('email')}
                onBlur={() => setFocusedField('')}
                required
                className="w-full h-14 pl-6 pr-6 bg-gray-800/50 border border-gray-600/50 rounded-2xl text-white placeholder-gray-400 focus:border-amber-500/70 focus:bg-gray-800/70 focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all duration-300"
              />
              <div className={`absolute left-6 top-1/2 transform -translate-y-1/2 transition-all duration-300 ${
                focusedField === 'email' || email ? 'opacity-100 scale-110' : 'opacity-0 scale-90'
              }`}>
                <div className="w-1 h-1 bg-amber-400 rounded-full"></div>
              </div>
            </div>
          </div>

          {/* Password Field */}
          <div className="space-y-2">
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Create password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setFocusedField('password')}
                onBlur={() => setFocusedField('')}
                required
                className="w-full h-14 pl-6 pr-14 bg-gray-800/50 border border-gray-600/50 rounded-2xl text-white placeholder-gray-400 focus:border-amber-500/70 focus:bg-gray-800/70 focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all duration-300"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-amber-400 transition-colors"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
              <div className={`absolute left-6 top-1/2 transform -translate-y-1/2 transition-all duration-300 ${
                focusedField === 'password' || password ? 'opacity-100 scale-110' : 'opacity-0 scale-90'
              }`}>
                <div className="w-1 h-1 bg-amber-400 rounded-full"></div>
              </div>
            </div>
          </div>

          {/* Security PIN Field - Conditional */}
          {(site_settings?.operational_settings?.request_pin_on_reg ?? 0) == 1 && (
            <div className="space-y-2">
              <div className="relative">
                <input
                  type={showPin ? 'text' : 'password'}
                  placeholder="Security PIN"
                  value={withdrawalPin}
                  onChange={(e) => setWithdrawalPin(e.target.value)}
                  onFocus={() => setFocusedField('pin')}
                  onBlur={() => setFocusedField('')}
                  maxLength={site_settings?.operational_settings?.security_pin_length}
                  required
                  className="w-full h-14 pl-6 pr-14 bg-gray-800/50 border border-gray-600/50 rounded-2xl text-white placeholder-gray-400 focus:border-amber-500/70 focus:bg-gray-800/70 focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all duration-300"
                />
                <button
                  type="button"
                  onClick={() => setShowPin(!showPin)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-amber-400 transition-colors"
                >
                  {showPin ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
                <div className={`absolute left-6 top-1/2 transform -translate-y-1/2 transition-all duration-300 ${
                  focusedField === 'pin' || withdrawalPin ? 'opacity-100 scale-110' : 'opacity-0 scale-90'
                }`}>
                  <div className="w-1 h-1 bg-amber-400 rounded-full"></div>
                </div>
              </div>
            </div>
          )}

          {/* Country Field */}
          <div className="space-y-2">
            <div className="relative">
              <select
                value={country}
                onChange={(e) => changeCountry(e.target.value)}
                onFocus={() => setFocusedField('country')}
                onBlur={() => setFocusedField('')}
                required
                className="w-full h-14 pl-6 pr-12 bg-gray-800/50 border border-gray-600/50 rounded-2xl text-white focus:border-amber-500/70 focus:bg-gray-800/70 focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all duration-300 appearance-none"
              >
                <option value="" className="bg-gray-800 text-gray-400">
                  Select your country
                </option>
                {staticCountries.map((item, index) => (
                  <option key={index} value={item.id} className="bg-gray-800 text-white">
                    {item.nicename}
                  </option>
                ))}
              </select>
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </div>
              <div className={`absolute left-6 top-1/2 transform -translate-y-1/2 transition-all duration-300 ${
                focusedField === 'country' || country ? 'opacity-100 scale-110' : 'opacity-0 scale-90'
              }`}>
                <div className="w-1 h-1 bg-amber-400 rounded-full"></div>
              </div>
            </div>
          </div>

          {/* Phone Field */}
          <div className="space-y-2">
            <div className="relative flex">
              <div className="flex items-center px-4 bg-gray-700/50 border border-gray-600/50 border-r-0 rounded-l-2xl text-gray-300 text-sm font-medium">
                {phoneCode}
              </div>
              <input
                type="tel"
                placeholder="Phone number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                onFocus={() => setFocusedField('phone')}
                onBlur={() => setFocusedField('')}
                required
                className="flex-1 h-14 pl-4 pr-6 bg-gray-800/50 border border-gray-600/50 rounded-r-2xl text-white placeholder-gray-400 focus:border-amber-500/70 focus:bg-gray-800/70 focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all duration-300"
              />
              <div className={`absolute right-6 top-1/2 transform -translate-y-1/2 transition-all duration-300 ${
                focusedField === 'phone' || phone ? 'opacity-100 scale-110' : 'opacity-0 scale-90'
              }`}>
                <div className="w-1 h-1 bg-amber-400 rounded-full"></div>
              </div>
            </div>
          </div>

          {/* Hidden referral field */}
          <input type="hidden" value={referral} onChange={(e) => setReferrals(e.target.value)} />

          {/* Terms Agreement - Conditional */}
          {(site_settings?.site_settings?.term ?? "") != "" && (
            <label className="flex items-start space-x-3 cursor-pointer group pt-2">
              <input
                type="checkbox"
                checked={agree}
                onChange={checkboxHandler}
                className="hidden"
              />
              <div className={`w-5 h-5 rounded-lg border-2 flex items-center justify-center transition-all duration-300 mt-0.5 ${
                agree 
                  ? 'bg-gradient-to-r from-amber-400 to-yellow-500 border-amber-500' 
                  : 'border-gray-500 group-hover:border-amber-400'
              }`}>
                {agree && (
                  <CheckCircle size={12} className="text-black" />
                )}
              </div>
              <span className="text-sm text-gray-300 group-hover:text-white transition-colors leading-relaxed">
                I agree to the{' '}
                <button
                  type="button"
                  onClick={handleShow}
                  className="text-amber-400 hover:text-amber-300 underline underline-offset-2"
                >
                  Terms of Service
                </button>
              </span>
            </label>
          )}

          {/* reCAPTCHA - Conditional rendering based on site settings */}
          {site_settings?.site_settings?.use_google_reCaptcha == 1 && (
            <div className="pt-2">
              <ReCAPTCHA onChange={captchaHasChanged} />
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full h-14 bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-600 hover:from-amber-500 hover:via-yellow-600 hover:to-amber-700 text-black font-semibold rounded-2xl transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl hover:shadow-amber-500/25 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 relative overflow-hidden group"
          >
            <div className="flex items-center justify-center relative z-10">
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-black border-t-transparent mr-3"></div>
                  {loadingText}
                </>
              ) : (
                <>
                  Launch Account
                  <ArrowRight className="ml-2 transition-transform group-hover:translate-x-1" size={20} />
                </>
              )}
            </div>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
          </button>
        </form>
      ) : (
        // Invitation Only Message
        <div className="text-center py-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-amber-400/20 to-yellow-500/20 rounded-2xl mb-6 border border-amber-400/30">
            <Info className="text-amber-400" size={32} />
          </div>
          <h3 className="text-2xl font-bold text-white mb-4">Registration by Invitation Only</h3>
          <p className="text-gray-400 mb-8 max-w-md mx-auto leading-relaxed">
            We're currently accepting new users by invitation only. Please contact an existing member for a referral link.
          </p>
          <Link
            to="/signin"
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-amber-400 to-yellow-500 text-black font-semibold rounded-2xl transition-all duration-300 hover:from-amber-500 hover:to-yellow-600 hover:scale-105 hover:shadow-xl hover:shadow-amber-500/25"
          >
            <ArrowLeft className="mr-2" size={20} />
            Return to Sign In
          </Link>
        </div>
      )}

      {/* Terms and Conditions Modal */}
      <Modal show={show} onHide={handleClose} centered>
        <div className="bg-gray-900 rounded-2xl shadow-2xl overflow-hidden border border-amber-500/20">
          <div className="flex items-center justify-between p-6 border-b border-gray-700/50">
            <h3 className="text-xl font-semibold text-white">Terms and Conditions</h3>
            <button 
              onClick={handleClose} 
              className="text-gray-400 hover:text-amber-400 focus:outline-none transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          <div className="p-6 max-h-[60vh] overflow-y-auto">
            <div className="prose prose-invert prose-amber max-w-none text-gray-300">
              {parse(site_settings?.site_settings?.term ?? "")}
            </div>
          </div>

          <div className="flex justify-end p-6 border-t border-gray-700/50">
            <button
              onClick={handleClose}
              className="px-6 py-3 bg-gradient-to-r from-amber-400 to-yellow-500 text-black font-semibold rounded-xl transition-all duration-300 hover:from-amber-500 hover:to-yellow-600 hover:scale-105"
            >
              I Understand
            </button>
          </div>
        </div>
      </Modal>
    </AuthHolder>
  )
}

export default SignupPage